import React, { useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { AddToDropdown } from "src/app/components/AddToDropdown/AddToDropdown";
import { ReactComponent as RocketIcon } from "src/images/rocket-grey.svg";
import { DropdownMenuItem } from "src/app/components/Dropdown/Dropdown";
import generateUuid from "src/app/libs/generateUuid";
import responseCodes from "src/utils/responseCodes";
import {
  generateSelectedInfluencersWithSocialProfiles,
  generateSocialProfiles,
  selectedCreatorsPayload,
} from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/SelectedCreatorsBar/utils";
import AddCreatorsCDTWorkspaceProjectModal from "src/app/modals/AddCreatorsCDTWorkspaceProjectModal/AddCreatorsCDTWorkspaceProjectModal";
import { createGlobalTasks } from "src/redux/creator-database/creatorDatabaseActions";
import { ReactComponent as CreatorIcon } from "src/images/creator-discovery.svg";
import { setCreateNewProjectData } from "src/redux/main/mainActions";
import { isFacebookNumericUsername, TaskType, SocialProvider } from "src/types";
import { handle402Errors } from "src/redux/main/functions";
import { API_URLS } from "src/utils/API_URLS";
import { showErrorToast, showSucessToast } from "src/utils/methods";
import { markCreatorsInCampaign } from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/CreatorSearch/redux/creatorSearchActions";

const ResearchProjectAddToDropdown = ({ handleOpenActivateProjectPane }) => {
  const [
    showModalAddCreatorsToWorkspaceProject,
    setShowModalAddCreatorsToWorkspaceProject,
  ] = useState(false);
  const { workspaceUuid } = useParams();
  const dispatch = useDispatch();

  const {
    searchAudienceResult,
    selectedInfluencersOnResultsUrls,
    selectedInfluencersOnResults,
  } = useSelector((state) => state.creatorSearchReducer);

  const getSelectedNonGlobalInfluencers = () => {
    const results = [];
    selectedInfluencersOnResults.forEach((creatorId) => {
      const influData = searchAudienceResult.find(
        (result) => result.userId === creatorId,
      );
      if (influData && !influData.wsGlobalTaskUuid) {
        const { socialProviders } = influData;
        const globalTaskData = {
          uuid: generateUuid(),
          name: influData.socialUsername,
          displayName: influData?.name ?? null,
          socialProfiles: generateSocialProfiles(socialProviders),
        };
        results.push(globalTaskData);
      }
    });
    return results;
  };

  const selectedNonGlobalInfluencers = getSelectedNonGlobalInfluencers();

  const dispatchDataAndOpenActivateProjectPane = () => {
    setShowModalAddCreatorsToWorkspaceProject(false);
    dispatch(
      setCreateNewProjectData({
        selectedCreatorsPayload: selectedCreatorsPayload(
          selectedInfluencersOnResultsUrls,
          searchAudienceResult,
        ),
      }),
    );
    handleOpenActivateProjectPane();
  };

  const addCreatorsToDatabase = () => {
    dispatch(
      createGlobalTasks(workspaceUuid, "creator", selectedNonGlobalInfluencers),
    ).then((res) => {
      if (res?.payload?.status === responseCodes["200_OK"]) {
        showSucessToast({
          id: "ws_creators_added_to_database",
          defaultMessage: "Creators successfully added to Database",
        });
      }
    });
  };

  const addCreatorsToProjectDisabled =
    selectedInfluencersOnResultsUrls.length < 1 ||
    selectedInfluencersOnResultsUrls.length > 100;
  return (
    <>
      <AddToDropdown>
        {(closeDropdown) => (
          <>
            <MaterialTooltip
              center
              content={
                selectedInfluencersOnResultsUrls.length > 100 ? (
                  <IDHFormattedMessage
                    id="front_you_can_add_only_100_influencers"
                    defaultMessage="You can add only 100 influencers to campaign at once"
                  />
                ) : (
                  <IDHFormattedMessage
                    id="front_select_creator_to_add_to_workspace_campaign"
                    defaultMessage="Select at least one Creator to add to the Campaign"
                  />
                )
              }
              contentHidden={!addCreatorsToProjectDisabled}
            >
              <DropdownMenuItem
                onClick={() => {
                  setShowModalAddCreatorsToWorkspaceProject(true);
                  closeDropdown();
                }}
                disabled={addCreatorsToProjectDisabled}
                data-qa="add-to-campaign-dropdown-menu-item"
              >
                <RocketIcon />
                <IDHFormattedMessage
                  id="front_campaign"
                  defaultMessage="Campaign"
                />
              </DropdownMenuItem>
            </MaterialTooltip>
            <MaterialTooltip
              center
              content={
                <IDHFormattedMessage
                  id="front_select_non_global_creator_to_add_to_database"
                  defaultMessage="Select at least one local Creator to add to the Creator Database"
                />
              }
              contentHidden={selectedNonGlobalInfluencers.length}
            >
              <DropdownMenuItem
                onClick={() => {
                  addCreatorsToDatabase();
                  closeDropdown();
                }}
                disabled={!selectedNonGlobalInfluencers.length}
                data-qa="add-to-creator-database-dropdown-menu-item"
              >
                <CreatorIcon />
                <IDHFormattedMessage
                  id="front_creator_database"
                  defaultMessage="Creator Database"
                />
              </DropdownMenuItem>
            </MaterialTooltip>
          </>
        )}
      </AddToDropdown>
      {showModalAddCreatorsToWorkspaceProject && (
        <AddCreatorsCDTWorkspaceProjectModal
          onClose={() => setShowModalAddCreatorsToWorkspaceProject(false)}
          activeWorkspaceUuid={workspaceUuid}
          selectedCreators={generateSelectedInfluencersWithSocialProfiles(
            selectedInfluencersOnResultsUrls,
            searchAudienceResult,
          )}
          openCreateNewProjectModal={dispatchDataAndOpenActivateProjectPane}
        />
      )}
    </>
  );
};

const ActiveProjectAddToDropdown = () => {
  const [loading, setLoading] = useState(false);
  const { workspaceUuid, projectId } = useParams();

  const dispatch = useDispatch();

  const {
    searchAudienceResult,
    selectedInfluencersOnResultsUrls,
    selectedInfluencersOnResults,
  } = useSelector((state) => state.creatorSearchReducer);

  const handleClick = () => {
    const selectedInfluencersWithSocialProfiles =
      generateSelectedInfluencersWithSocialProfiles(
        selectedInfluencersOnResultsUrls,
        searchAudienceResult,
      );
    const tasksData = selectedInfluencersWithSocialProfiles.map((creator) => {
      const isFacebookNumericName =
        creator.socialProfiles &&
        creator.socialProfiles[0].socialProvider === SocialProvider.Facebook &&
        isFacebookNumericUsername(creator.name);

      if (creator.socialProfiles) {
        return {
          uuid: generateUuid(),
          name: creator.name,
          socialProfiles: creator.socialProfiles,
          rank: null,
          displayName: isFacebookNumericName ? creator?.displayName : null,
        };
      }

      return {
        uuid: generateUuid(),
        name: creator,
        rank: null,
        displayName: isFacebookNumericName ? creator?.displayName : null,
      };
    });

    const payload = {
      wsTaskWorkspaceUuid: workspaceUuid,
      projectId: projectId,
      taskType: TaskType.Creator,
      tasksData,
    };

    setLoading(true);

    axios
      .post(API_URLS.createMultipleTasks, payload)
      .then(() => {
        dispatch(markCreatorsInCampaign(selectedInfluencersOnResults));
        showSucessToast({
          id: "ws_success_adding_creators_to_campaign",
          defaultMessage:
            "Successfully added Creators to the selected campaign.",
        });
      })
      .catch((err) => {
        if (
          err?.response?.data?.statusCode ===
          responseCodes["402_PAYMENT_REQUIRED"]
        ) {
          handle402Errors(dispatch, err?.response?.data?.errors ?? []);
        } else {
          showErrorToast({
            id: "ws_error_adding_creators_to_campaign",
            defaultMessage: "Error adding Creators to the selected campaign.",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const addCreatorsToProjectDisabled =
    selectedInfluencersOnResultsUrls.length < 1 ||
    selectedInfluencersOnResultsUrls.length > 100;

  return (
    <AddToDropdown>
      {(closeDropdown) => (
        <MaterialTooltip
          center
          content={
            selectedInfluencersOnResultsUrls.length > 100 ? (
              <IDHFormattedMessage
                id="front_you_can_add_only_100_influencers"
                defaultMessage="You can add only 100 influencers to campaign at once"
              />
            ) : (
              <IDHFormattedMessage
                id="front_select_creator_to_add_to_workspace_campaign"
                defaultMessage="Select at least one Creator to add to the Campaign"
              />
            )
          }
          contentHidden={!addCreatorsToProjectDisabled}
        >
          <DropdownMenuItem
            onClick={() => {
              handleClick();
              closeDropdown();
            }}
            disabled={addCreatorsToProjectDisabled || loading}
            data-qa="add-to-campaign-dropdown-menu-item"
          >
            <RocketIcon />
            <IDHFormattedMessage
              id="ws_this_campaign"
              defaultMessage="This campaign"
            />
          </DropdownMenuItem>
        </MaterialTooltip>
      )}
    </AddToDropdown>
  );
};

export default function CreatorSearchAddToDropdown({
  handleOpenActivateProjectPane,
}) {
  const { projectBasicData } = useSelector((state) => state.projectReducer);
  const projectStatus = projectBasicData?.status;

  if (projectStatus === "research") {
    return (
      <ResearchProjectAddToDropdown
        handleOpenActivateProjectPane={handleOpenActivateProjectPane}
      />
    );
  } else {
    return <ActiveProjectAddToDropdown />;
  }
}
